/**
 * Shared viewport math for surface-modal tools (Surface Box, Surface Poly).
 *
 * Both tools need to convert a screen-space pointer position into a
 * world-space parameter on a face-locked basis: either a hit point on the
 * basis plane (drawing stage) or a signed distance along the basis normal
 * (extrude stage). Lifted out of `SurfaceBoxHandler` so the two handlers
 * share one tested implementation instead of carrying parallel copies.
 *
 * THREE-dependent (uses `Camera`, `Vector3`) — lives in `modeling/` for
 * locality with `faceBasis.ts`, but kept off the pure-math path so
 * `faceBasis.ts` stays free of a renderer dependency.
 */

import * as THREE from "three/webgpu";
import type { AppState, AxisConstraint } from "../app/storeTypes.ts";
import type { FaceBasis } from "./faceBasis.ts";
import { applySnapping } from "./snap.ts";

/**
 * Intersect the ray through `(ndcX, ndcY)` with the basis plane.
 *
 * Returns `null` only when the camera direction is parallel to the plane
 * (the ray never hits). Callers typically keep their previous corner in
 * that degenerate case rather than producing a NaN write.
 *
 * `scratchOrigin` and `scratchDirection` are mutated. `out` is the
 * returned position when non-null.
 */
export function intersectRayWithBasisPlane(
  ndcX: number,
  ndcY: number,
  camera: THREE.Camera,
  basis: FaceBasis,
  out: THREE.Vector3,
  scratchOrigin: THREE.Vector3,
  scratchDirection: THREE.Vector3,
): THREE.Vector3 | null {
  scratchOrigin.set(ndcX, ndcY, 0).unproject(camera);
  scratchDirection
    .set(ndcX, ndcY, 1)
    .unproject(camera)
    .sub(scratchOrigin)
    .normalize();
  const denom =
    scratchDirection.x * basis.normal[0] +
    scratchDirection.y * basis.normal[1] +
    scratchDirection.z * basis.normal[2];
  if (Math.abs(denom) < 1e-9) return null;
  const numer =
    (basis.origin[0] - scratchOrigin.x) * basis.normal[0] +
    (basis.origin[1] - scratchOrigin.y) * basis.normal[1] +
    (basis.origin[2] - scratchOrigin.z) * basis.normal[2];
  const t = numer / denom;
  if (!Number.isFinite(t)) return null;
  out.set(
    scratchOrigin.x + scratchDirection.x * t,
    scratchOrigin.y + scratchDirection.y * t,
    scratchOrigin.z + scratchDirection.z * t,
  );
  return out;
}

const _NORMAL_AXIS: AxisConstraint = "normal";
const _heightScratch = new THREE.Vector3();
const _heightOrigin = new THREE.Vector3();
const _heightDirection = new THREE.Vector3();

/**
 * Project a pointer onto a world-space axis through `anchor` along `normal`,
 * apply normal-axis snap, and return the signed scalar distance.
 *
 * Math: closest-point pair between the line `anchor + t·normal` and the
 * pointer ray; collapses cleanly to an axis-projected origin when the ray
 * is parallel to the axis. After computing `t`, runs `applySnapping` with
 * `axis="normal"` so vertex / edge / face / surface / grid snap all
 * participate exactly the way they do for extrude.
 *
 * Both Surface Box's height stage and Surface Poly's extrude stage use
 * this directly — single source of truth for "drag along the face normal".
 */
export function pointerToSignedHeightAlongNormal(
  ndcX: number,
  ndcY: number,
  camera: THREE.Camera,
  rect: DOMRect,
  anchor: readonly [number, number, number],
  normal: readonly [number, number, number],
  state: AppState,
  excludeObjectIds: Set<string>,
  clientX: number,
  clientY: number,
): number {
  _heightOrigin.set(ndcX, ndcY, 0).unproject(camera);
  _heightDirection
    .set(ndcX, ndcY, 1)
    .unproject(camera)
    .sub(_heightOrigin)
    .normalize();

  const dx = _heightOrigin.x - anchor[0];
  const dy = _heightOrigin.y - anchor[1];
  const dz = _heightOrigin.z - anchor[2];

  const nx = normal[0];
  const ny = normal[1];
  const nz = normal[2];
  const dDotN =
    _heightDirection.x * nx + _heightDirection.y * ny + _heightDirection.z * nz;
  const denom = 1 - dDotN * dDotN;
  let t: number;
  if (denom < 1e-9) {
    // Ray nearly parallel to the axis — fall back to projecting the ray
    // origin onto the axis. Stable degenerate case.
    t = dx * nx + dy * ny + dz * nz;
  } else {
    // Closest-point pair between the axis line and the pointer ray:
    //   t = ((origin − anchor)·n − ((origin − anchor)·d)(d·n)) / (1 − (d·n)²)
    const oDotD =
      dx * _heightDirection.x +
      dy * _heightDirection.y +
      dz * _heightDirection.z;
    const oDotN = dx * nx + dy * ny + dz * nz;
    t = (oDotN - oDotD * dDotN) / denom;
  }

  _heightScratch.set(t * nx, t * ny, t * nz);
  applySnapping(
    state,
    clientX,
    clientY,
    rect,
    anchor as [number, number, number],
    _heightScratch,
    _NORMAL_AXIS,
    [nx, ny, nz],
    excludeObjectIds,
    0,
  );
  return _heightScratch.x * nx + _heightScratch.y * ny + _heightScratch.z * nz;
}
